/* eslint-disable @typescript-eslint/no-explicit-any */
import DOMPurify from 'isomorphic-dompurify';

/**
 * Shop-renderer specific shims for AEM clientlib compatibility.
 *
 * These shims handle edge cases where AEM clientlibs fail to properly
 * initialize modals (sign-in, member panel) in the Next.js environment.
 *
 * This includes:
 * - Fetch interceptor for .mi endpoints (graceful error handling)
 * - Dialog click handler fallback (when clientlib doesn't fire)
 * - Header re-render detection (for login state updates)
 * - Login success detection (for iframe-based sign-in)
 * - DOMPurify shim (for clientlib compatibility)
 */

declare global {
  interface Window {
    DOMPurify: any;
    __fetchIntercepted?: boolean;
    __lastMiFetchTimestamp?: number;
    __pendingDialogId?: string | null;
    __clickListenerAttached?: boolean;
    __headerRefreshSetup?: boolean;
  }
}

/**
 * Initializes all shop-specific AEM clientlib shims.
 * Call this early in _app.tsx before any AEM clientlibs run.
 */
export function initShopAemShims(): void {
  if (typeof window === 'undefined') return;

  setupDOMPurifyShim();
  setupHeaderRefreshDetection();
  setupFetchInterceptor();
  setupDialogClickHandler();
}

/**
 * Sets up a DOMPurify shim for AEM clientlib compatibility.
 * The clientlib may access DOMPurify via window, require, or import.
 */
function setupDOMPurifyShim(): void {
  if (typeof window === 'undefined') return;

  const domPurifyShim = {
    sanitize: (dirty: string) => dirty || '',
    setConfig: () => {},
    clearConfig: () => {},
    isValidAttribute: () => true,
    addHook: () => {},
    removeHook: () => {},
    removeHooks: () => {},
    removeAllHooks: () => {},
    isSupported: true,
    version: '3.0.0-shim',
  };

  if (!window.DOMPurify) {
    window.DOMPurify = domPurifyShim;
  }

  if (typeof globalThis !== 'undefined' && !(globalThis as any).DOMPurify) {
    (globalThis as any).DOMPurify = domPurifyShim;
  }
}

/**
 * Sets up header re-render detection and auto-refresh.
 * When React re-renders the header (via dangerouslySetInnerHTML),
 * this triggers DOMContentLoaded to reinitialize the clientlib.
 */
function setupHeaderRefreshDetection(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__headerRefreshSetup) return;

  window.__headerRefreshSetup = true;

  let headerRefreshTimeout: ReturnType<typeof setTimeout> | null = null;
  let lastHeaderRefreshTime = 0;
  const REFRESH_COOLDOWN = 2000;

  const originalInnerHTMLDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
  if (originalInnerHTMLDescriptor && originalInnerHTMLDescriptor.set) {
    const originalSetter = originalInnerHTMLDescriptor.set;
    Object.defineProperty(Element.prototype, 'innerHTML', {
      ...originalInnerHTMLDescriptor,
      set: function (value: string) {
        if (value && typeof value === 'string' && value.includes('m-header')) {
          const now = Date.now();
          if (now - lastHeaderRefreshTime > REFRESH_COOLDOWN) {
            if (headerRefreshTimeout) {
              clearTimeout(headerRefreshTimeout);
            }

            // Hide sign-in element to prevent flash
            const hideSignInElement = () => {
              const signInElement = document.querySelector(
                '.utility-item.utility-signin, .utility-signin, [data-component-name="utility-signin"]'
              );
              if (signInElement) {
                (signInElement as HTMLElement).style.visibility = 'hidden';
              }
            };
            hideSignInElement();

            headerRefreshTimeout = setTimeout(() => {
              lastHeaderRefreshTime = Date.now();
              document.dispatchEvent(new Event('DOMContentLoaded'));

              // Show sign-in element after clientlib updates it
              setTimeout(() => {
                const signInElement = document.querySelector(
                  '.utility-item.utility-signin, .utility-signin, [data-component-name="utility-signin"]'
                );
                if (signInElement) {
                  (signInElement as HTMLElement).style.visibility = '';
                }
              }, 300);

              headerRefreshTimeout = null;
            }, 500);
          }
        }
        return originalSetter.call(this, value);
      },
    });
  }

  // Check for pending header refresh from login redirect
  checkPendingHeaderRefresh();
}

/**
 * Checks if we need to refresh the header after a login redirect.
 */
function checkPendingHeaderRefresh(): void {
  if (typeof sessionStorage === 'undefined') return;

  try {
    const pendingRefresh = sessionStorage.getItem('__pendingHeaderRefresh');
    if (pendingRefresh === 'true') {
      sessionStorage.removeItem('__pendingHeaderRefresh');
      setTimeout(() => {
        document.dispatchEvent(new Event('DOMContentLoaded'));
      }, 1000);
    }
  } catch {
    // Ignore sessionStorage errors
  }
}

/**
 * Validates that a URL's hostname belongs to marriott.com.
 */
function isMarriottHost(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    const hostname = parsed.hostname.toLowerCase();
    return hostname === 'marriott.com' || hostname.endsWith('.marriott.com');
  } catch {
    return false;
  }
}

/**
 * Intercepts fetch to handle .mi endpoint failures gracefully.
 * Shows a user-friendly error modal instead of silent failures.
 */
function setupFetchInterceptor(): void {
  if (typeof window === 'undefined') return;
  if (window.__fetchIntercepted) return;

  window.__fetchIntercepted = true;
  const originalFetch = window.fetch;

  window.fetch = async function (...args: Parameters<typeof fetch>) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;

    // Check if this is a Marriott .mi endpoint request (modal overlay content)
    const isMiEndpoint = (url.endsWith('.mi') || url.includes('.mi?')) && (url.startsWith('/') || isMarriottHost(url));

    if (isMiEndpoint) {
      window.__lastMiFetchTimestamp = Date.now();
      const response = await originalFetch.apply(this, args);
      return response;
    }

    return originalFetch.apply(this, args);
  };
}

/**
 * Listens for modal trigger clicks and provides fallback if clientlib doesn't handle them.
 */
function setupDialogClickHandler(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__clickListenerAttached) return;

  window.__clickListenerAttached = true;

  document.addEventListener(
    'click',
    e => {
      const target = e.target as HTMLElement;
      const dialogTrigger = target.closest('[data-dialog-id]') as HTMLElement | null;

      if (dialogTrigger) {
        const dialogId = dialogTrigger.getAttribute('data-dialog-id');
        const overlayEndpoint = dialogTrigger.getAttribute('data-overlay-endpoint');

        // Check if user is authenticated - skip fallback for member panel
        const isAuthenticated =
          (window as any).s_loginState === 'authenticated' ||
          document.querySelector('.m-header')?.classList.contains('authenticated') ||
          document.cookie.includes('UserIdToken');

        if (isAuthenticated && !overlayEndpoint) {
          return;
        }

        const clickTimestamp = Date.now();
        window.__pendingDialogId = dialogId;

        // Check after 100ms if the clientlib handler fired
        setTimeout(() => {
          const fetchHappened = window.__lastMiFetchTimestamp && window.__lastMiFetchTimestamp > clickTimestamp - 50;

          const modalElement = dialogId ? document.getElementById(dialogId) : null;
          const isModalOpen =
            modalElement &&
            (modalElement.classList.contains('mdc-dialog--open') ||
              modalElement.classList.contains('show') ||
              modalElement.getAttribute('aria-hidden') === 'false');

          if (isModalOpen) {
            window.__pendingDialogId = null;
            return;
          }

          const shouldTriggerFallback =
            window.__pendingDialogId === dialogId && overlayEndpoint && (!fetchHappened || !isModalOpen);

          if (shouldTriggerFallback) {
            fetchOverlayManually(overlayEndpoint, dialogId);
          }
          window.__pendingDialogId = null;
        }, 100);
      }
    },
    true
  );
}

/**
 * Sets up detection for successful login when using the fallback modal.
 */
function setupLoginSuccessDetection(modalElement: HTMLElement): void {
  const setPendingRefresh = () => {
    try {
      sessionStorage.setItem('__pendingHeaderRefresh', 'true');
    } catch {
      // Ignore sessionStorage errors
    }
  };

  // Try to find and attach listeners to the sign-in iframe
  const attachIframeListeners = () => {
    const iframe = modalElement.querySelector('iframe') as HTMLIFrameElement | null;

    if (iframe) {
      try {
        const setupIframeListeners = () => {
          try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

            if (iframeDoc) {
              iframeDoc.addEventListener('submit', () => setPendingRefresh(), true);

              iframeDoc.addEventListener(
                'click',
                (e: Event) => {
                  const target = e.target as HTMLElement;
                  const submitBtn =
                    target.closest('button[type="submit"]') ||
                    target.closest('input[type="submit"]') ||
                    target.closest('.signin-submit') ||
                    target.closest('[data-submit]') ||
                    target.closest('.m-button-primary') ||
                    target.closest('.m-button');

                  if (submitBtn) {
                    setPendingRefresh();
                  }
                },
                true
              );
            }
          } catch {
            setPendingRefresh();
          }
        };

        if (iframe.contentDocument?.readyState === 'complete') {
          setupIframeListeners();
        } else {
          iframe.addEventListener('load', setupIframeListeners);
        }
      } catch {
        setPendingRefresh();
      }
    }
  };

  // Try immediately and after delays (iframe may load after modal content)
  attachIframeListeners();
  setTimeout(attachIframeListeners, 500);
  setTimeout(attachIframeListeners, 1000);
  setTimeout(attachIframeListeners, 2000);

  // Watch for form submissions within the modal itself
  modalElement.addEventListener('submit', () => setPendingRefresh(), true);

  modalElement.addEventListener(
    'click',
    (e: Event) => {
      const target = e.target as HTMLElement;
      const submitBtn =
        target.closest('button[type="submit"]') ||
        target.closest('input[type="submit"]') ||
        target.closest('.signin-submit') ||
        target.closest('[data-submit]') ||
        target.closest('.m-button-primary');

      if (submitBtn) {
        setPendingRefresh();
      }
    },
    true
  );

  // Watch for login-related link clicks
  modalElement.addEventListener(
    'click',
    (e: Event) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement | null;
      if (link && (link.href.includes('login') || link.href.includes('signin') || link.href.includes('auth'))) {
        setPendingRefresh();
      }
    },
    true
  );

  // Track login interactions for beforeunload
  let loginInteractionDetected = false;

  modalElement.addEventListener(
    'click',
    (e: Event) => {
      const target = e.target as HTMLElement;
      if (
        target.closest('button') ||
        target.closest('input[type="submit"]') ||
        target.closest('a[href*="login"]') ||
        target.closest('a[href*="signin"]')
      ) {
        loginInteractionDetected = true;
      }
    },
    true
  );

  const beforeUnloadHandler = () => {
    if (loginInteractionDetected) {
      setPendingRefresh();
    }
  };
  window.addEventListener('beforeunload', beforeUnloadHandler);

  // Clean up when modal closes
  const cleanupObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        if (!modalElement.classList.contains('mdc-dialog--open')) {
          try {
            sessionStorage.removeItem('__pendingHeaderRefresh');
          } catch {
            // Ignore sessionStorage errors
          }
          window.removeEventListener('beforeunload', beforeUnloadHandler);
          cleanupObserver.disconnect();
        }
      }
    }
  });

  cleanupObserver.observe(modalElement, { attributes: true });
}

/**
 * Manually fetches the overlay content and displays it or shows an error.
 */
async function fetchOverlayManually(url: string, dialogId: string | null): Promise<void> {
  try {
    const response = await fetch(url);
    window.__lastMiFetchTimestamp = Date.now();

    if (!response.ok) {
      return;
    }

    const html = await response.text();
    const modalElement = dialogId ? document.getElementById(dialogId) : null;

    if (modalElement) {
      const surface = modalElement.querySelector('.mdc-dialog__surface');

      if (surface) {
        let contentHtml = html;

        // Extract body content from full HTML document
        if (html.includes('<!DOCTYPE') || html.includes('<html')) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const contentSelectors = ['.modal-content', '.mfp-content', '.header-overlay', '.signin', 'main', 'body'];
          let extractedContent: Element | null = null;
          for (const selector of contentSelectors) {
            extractedContent = doc.querySelector(selector);
            if (extractedContent && extractedContent.innerHTML.trim()) {
              break;
            }
          }
          contentHtml = extractedContent?.innerHTML || doc.body?.innerHTML || html;
        }

        const closeButtonHtml = `
          <button class="m-dialog-close-btn icon-clear m-dialog-close-btn-fallback" aria-label="Close">
            <span class="sr-only">Close Dialog</span>
          </button>
        `;
        const sanitizedSurfaceFragment = DOMPurify.sanitize(
          closeButtonHtml + `<div class="mdc-dialog__content">${contentHtml}</div>`,
          { ADD_ATTR: ['aria-label'], RETURN_DOM_FRAGMENT: true }
        ) as unknown as DocumentFragment;
        while (surface.firstChild) {
          surface.removeChild(surface.firstChild);
        }
        surface.appendChild(sanitizedSurfaceFragment);

        // Add close handlers
        const fallbackCloseBtn = surface.querySelector('.m-dialog-close-btn-fallback');
        if (fallbackCloseBtn) {
          fallbackCloseBtn.addEventListener('click', () => {
            modalElement.classList.remove('mdc-dialog--open');
            document.body.classList.remove('mdc-dialog-scroll-lock');
            document.body.style.overflow = '';
          });
        }

        const existingCloseBtn = surface.querySelector(
          '.m-dialog-close-btn, .icon-clear, [data-dialog-close], [aria-label="Close"]:not(.m-dialog-close-btn-fallback)'
        );
        if (existingCloseBtn) {
          existingCloseBtn.addEventListener('click', () => {
            modalElement.classList.remove('mdc-dialog--open');
            document.body.classList.remove('mdc-dialog-scroll-lock');
            document.body.style.overflow = '';
          });
        }

        modalElement.classList.add('mdc-dialog--open');
        document.body.classList.add('mdc-dialog-scroll-lock');

        // Set up login detection for sign-in modals
        const isSignInModal = dialogId?.includes('signin') || url.includes('signIn');
        if (isSignInModal) {
          setupLoginSuccessDetection(modalElement);
        }

        // Backdrop click to close
        const backdropHandler = (e: Event) => {
          const target = e.target as HTMLElement;
          if (
            target === modalElement ||
            target.classList.contains('mdc-dialog__scrim') ||
            target.classList.contains('mdc-dialog__container')
          ) {
            modalElement.classList.remove('mdc-dialog--open');
            document.body.classList.remove('mdc-dialog-scroll-lock');
            document.body.style.overflow = '';
            modalElement.removeEventListener('click', backdropHandler);
          }
        };
        modalElement.addEventListener('click', backdropHandler);
      } else {
        // Try other content container selectors
        const contentSelectors = ['.mdc-dialog__content', '.modal-body', '.modal-content', '.dialog-content'];
        let contentContainer: Element | null = null;
        for (const selector of contentSelectors) {
          contentContainer = modalElement.querySelector(selector);
          if (contentContainer) break;
        }

        if (contentContainer) {
          const sanitizedContentFragment = DOMPurify.sanitize(html, {
            ADD_ATTR: ['aria-label'],
            RETURN_DOM_FRAGMENT: true,
          }) as unknown as DocumentFragment;
          while (contentContainer.firstChild) {
            contentContainer.removeChild(contentContainer.firstChild);
          }
          contentContainer.appendChild(sanitizedContentFragment);
          modalElement.classList.add('mdc-dialog--open');
          document.body.classList.add('mdc-dialog-scroll-lock');
        }
      }
    }
  } catch (error) {
    console.error('[AEM Shim] Error in fetchOverlayManually:', error);
  }
}
